import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { CampaignGroup, LeadContact } from "../types/lead-contacts.types";
import ContactCard from "./ContactCard";

interface CampaignCardProps {
  campaignName: string;
  campaignContacts: CampaignGroup;
  isExpanded: boolean;
  isFullySelected: boolean;
  isPartiallySelected: boolean;
  campaignLoading: boolean;
  hasMore: boolean;
  onToggle: (campaignName: string) => void;
  onSelectAll: (campaignName: string) => void;
  onSelectLead: (leadId: number) => void;
  onLoadMore: (campaignName: string) => void;
  onLoadAll: (campaignName: string) => void;
  onCopyContact: (contact: LeadContact) => void;
  onCopyPhone: (phone: string) => void;
  onCopyLeadId: (leadId: number) => void;
  onShowDetails: (contact: LeadContact) => void;
  isLeadSelected: (leadId: number) => boolean;
  isLeadCopied: (leadId: number) => boolean;
  showCopiedLeads: boolean;
  searchText?: string;
  isSearchMatch?: boolean;
  isCurrentMatch?: boolean;
  highlightText?: (text: string, searchTerm: string) => React.ReactNode;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  campaignName,
  campaignContacts,
  isExpanded,
  isFullySelected,
  isPartiallySelected,
  campaignLoading,
  hasMore,
  onToggle,
  onSelectAll,
  onSelectLead,
  onLoadMore,
  onLoadAll,
  onCopyContact,
  onCopyPhone,
  onCopyLeadId,
  onShowDetails,
  isLeadSelected,
  isLeadCopied,
  showCopiedLeads,
  searchText = "",
  isSearchMatch = false,
  isCurrentMatch = false,
  highlightText,
}) => {
  const filteredContacts = useMemo(() => {
    const filtered = campaignContacts.contacts.filter(
      (contact: LeadContact) => {
        if (!contact || !contact.id) return false;

        // Usar o ID correto baseado no tipo de lead (qualidade ou normal)
        const actualLeadId = contact.is_quality_lead
          ? contact.id
          : contact.lead_id;
        const isLeadAlreadyCopied = isLeadCopied(Number(actualLeadId));
        const shouldHide = !showCopiedLeads && isLeadAlreadyCopied;

        return !shouldHide;
      },
    );

    return filtered;
  }, [campaignContacts.contacts, isLeadCopied, showCopiedLeads, campaignName]);

  const copiedCount = useMemo(() => {
    // Usar o valor do servidor como base (reflete todos os leads da campanha no DB)
    const serverCopied = campaignContacts.copied_total ?? 0;

    const loadedCopied = campaignContacts.contacts.filter((contact) => {
      const actualLeadId = contact.is_quality_lead
        ? contact.id
        : contact.lead_id;
      return isLeadCopied(actualLeadId);
    }).length;

    // Retornamos o que for maior (pois o servidor pode ter mais copiados do que carregados na tela)
    return Math.max(serverCopied, loadedCopied);
  }, [campaignContacts.contacts, isLeadCopied, campaignContacts.copied_total]);

  const totalInCampaign =
    campaignContacts.total ?? campaignContacts.contacts.length;
  const remainingCount = Math.max(totalInCampaign - copiedCount, 0);

  return (
    <Box
      data-campaign-name={campaignName}
      sx={{
        "@keyframes pulse": {
          "0%, 100%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.5,
          },
        },
        "@keyframes spin": {
          "0%": {
            transform: "rotate(0deg)",
          },
          "100%": {
            transform: "rotate(360deg)",
          },
        },
        border: 1,
        borderColor: isCurrentMatch
          ? "#28a745"
          : isSearchMatch
            ? "#008069"
            : "#e0e0e0",
        borderLeftWidth: isCurrentMatch || isSearchMatch ? 4 : 1,
        borderRadius: 2,
        mb: 1.5,
        overflow: "hidden",
        boxShadow: isCurrentMatch
          ? "0 4px 12px rgba(40, 167, 69, 0.3)"
          : isSearchMatch
            ? "0 2px 8px rgba(0, 128, 105, 0.2)"
            : "0 2px 4px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease",
        display: "flex",
        flexDirection: "column",
        minHeight: "50px",
        position: "relative",
        backgroundColor: isCurrentMatch
          ? "#d4edda"
          : isSearchMatch
            ? "#f0f8ff"
            : "transparent",
        "&:hover": {
          boxShadow: isCurrentMatch
            ? "0 4px 12px rgba(40, 167, 69, 0.4)"
            : isSearchMatch
              ? "0 4px 12px rgba(0, 128, 105, 0.3)"
              : "0 4px 8px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {/* Header da Campanha */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          background: "linear-gradient(135deg, #008069 0%, #006d5b 100%)",
          color: "white",
          borderRadius: "8px 8px 0 0",
          cursor: "pointer",
          transition: "all 0.2s ease",
          minHeight: "40px",
          "&:hover": {
            background: "linear-gradient(135deg, #006d5b 0%, #005a4a 100%)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Tooltip title="Campanhas ordenadas pelas mais recentes">
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "13px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                📋{" "}
                {highlightText && searchText
                  ? highlightText(campaignName, searchText)
                  : campaignName}
                {campaignLoading && (
                  <CircularProgress
                    size={14}
                    sx={{
                      color: "white",
                      marginLeft: 0.5,
                    }}
                  />
                )}
              </Typography>
            </Tooltip>
            <Typography
              sx={{
                fontSize: "11px",
                opacity: 0.9,
                fontWeight: 500,
                whiteSpace: "nowrap",
                marginLeft: 1,
                lineHeight: 1.2,
              }}
            >
              {copiedCount} / {campaignContacts.total}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Checkbox para selecionar toda a campanha */}
          <Tooltip
            title={
              isFullySelected
                ? "Desmarcar todos da campanha"
                : "Selecionar todos da campanha"
            }
          >
            <Checkbox
              checked={isFullySelected}
              indeterminate={isPartiallySelected}
              onChange={() => onSelectAll(campaignName)}
              sx={{
                color: "white",
                "&.Mui-checked": {
                  color: "white",
                },
                "&.MuiCheckbox-indeterminate": {
                  color: "white",
                },
                padding: "2px",
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              campaignLoading
                ? ""
                : isExpanded
                  ? "Recolher campanha"
                  : "Expandir campanha"
            }
          >
            <Box component="span">
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  flexShrink: 0,
                  padding: "4px",
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
                onClick={() => !campaignLoading && onToggle(campaignName)}
                disabled={campaignLoading}
              >
                {campaignLoading ? (
                  <CircularProgress
                    size={18}
                    sx={{
                      color: "white",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      transition: "transform 0.2s ease",
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ExpandMoreIcon />
                  </Box>
                )}
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Conteúdo da Campanha */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            height: "250px",
            padding: 1,
            paddingBottom: "60px",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f8f9fa",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1,
              py: 0.75,
              mb: 1,
              borderRadius: 1,
              backgroundColor: "#f5f7f8",
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#111b21" }}
            >
              Copiados: {copiedCount}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#54656f" }}
            >
              Restantes: {remainingCount}
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginLeft: "auto", color: "#667781" }}
            >
              Total: {totalInCampaign}
            </Typography>
          </Box>
          <List>
            {campaignLoading ? (
              // Skeleton loader enquanto carrega - mostrar sempre quando está carregando
              campaignContacts.contacts.length === 0 ? (
                <Box sx={{ padding: 1 }}>
                  {[1, 2, 3].map((index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        backgroundColor: "#f5f7f8",
                        border: "1px solid #e0e0e0",
                        animation: "pulse 1.5s ease-in-out infinite",
                        animationDelay: `${index * 0.2}s`,
                      }}
                    >
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={20}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                // Se já tem alguns contatos mas está carregando mais, mostrar os existentes + skeleton no final
                <>
                  {filteredContacts.map(
                    (contact: LeadContact, contactIndex: number) => (
                      <ListItem
                        key={contact.id}
                        sx={{
                          width: "100%",
                          padding: 0,
                          marginBottom: 0.5,
                          animation: "fadeIn 0.2s ease-out",
                          animationDelay: `${contactIndex * 50}ms`,
                          animationFillMode: "both",
                        }}
                      >
                        <ContactCard
                          contact={contact}
                          isSelected={isLeadSelected(contact.id)}
                          isCopied={isLeadCopied(
                            contact.is_quality_lead
                              ? contact.id
                              : contact.lead_id,
                          )}
                          onSelect={onSelectLead}
                          onCopyContact={onCopyContact}
                          onCopyPhone={onCopyPhone}
                          onCopyLeadId={onCopyLeadId}
                          onShowDetails={onShowDetails}
                          searchText={searchText}
                          highlightText={highlightText}
                        />
                      </ListItem>
                    ),
                  )}
                  <Box sx={{ padding: 1, pt: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: 1.5,
                        borderRadius: 1,
                        backgroundColor: "#f5f7f8",
                        border: "1px solid #e0e0e0",
                        animation: "pulse 1.5s ease-in-out infinite",
                      }}
                    >
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={20}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  </Box>
                </>
              )
            ) : filteredContacts.length === 0 ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  color: "#0f4b5f",
                  backgroundColor: "#f4fbff",
                  borderColor: "#b9e3f4",
                  "& .MuiAlert-icon": { color: "#0f4b5f" },
                }}
              >
                {campaignContacts.total > 0 && !showCopiedLeads
                  ? remainingCount === 0
                    ? 'Todos os contatos desta campanha já foram copiados e estão ocultos. Use o controle "Mostrar" acima para visualizá-los.'
                    : 'Os contatos carregados até agora já foram copiados e estão ocultos. Carregue mais para encontrar os restantes ou use o controle "Mostrar" acima para visualizá-los.'
                  : "Nenhum contato disponível nesta campanha."}
              </Alert>
            ) : (
              filteredContacts.map(
                (contact: LeadContact, contactIndex: number) => (
                  <ListItem
                    key={contact.id}
                    sx={{
                      width: "100%",
                      padding: 0,
                      marginBottom: 0.5,
                      animation: "fadeIn 0.2s ease-out",
                      animationDelay: `${contactIndex * 50}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <ContactCard
                      contact={contact}
                      isSelected={isLeadSelected(contact.id)}
                      isCopied={isLeadCopied(
                        contact.is_quality_lead ? contact.id : contact.lead_id,
                      )}
                      onSelect={onSelectLead}
                      onCopyContact={onCopyContact}
                      onCopyPhone={onCopyPhone}
                      onCopyLeadId={onCopyLeadId}
                      onShowDetails={onShowDetails}
                      searchText={searchText}
                      highlightText={highlightText}
                    />
                  </ListItem>
                ),
              )
            )}
          </List>
        </Box>

        {/* Botões "Carregar +10" e "Carregar todos" como rodapé do card */}
        {campaignContacts.contacts.length > 0 && hasMore && (
          <Box
            sx={{
              background: "white",
              borderTop: "1px solid #e0e0e0",
              padding: "12px",
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "auto",
              flexWrap: "wrap",
            }}
          >
            <Tooltip
              title={`Carregar mais ${Math.min(10, campaignContacts.total - campaignContacts.contacts.length)} contatos da campanha "${campaignName}"`}
            >
              <Box
                component="span"
                sx={{ flex: 1, maxWidth: "200px", display: "flex" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => onLoadMore(campaignName)}
                  disabled={campaignLoading}
                  fullWidth
                  startIcon={
                    campaignLoading ? <CircularProgress size={16} /> : null
                  }
                  sx={{
                    color: "#008069",
                    borderColor: "#008069",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    minWidth: "140px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 128, 105, 0.1)",
                      borderColor: "#006d5b",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(0, 128, 105, 0.15)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                    },
                  }}
                >
                  {campaignLoading ? "Carregando..." : `Carregar +10`}
                </Button>
              </Box>
            </Tooltip>
            <Tooltip
              title={`Carregar todos os ${campaignContacts.total - campaignContacts.contacts.length} contatos restantes da campanha "${campaignName}"`}
            >
              <Box
                component="span"
                sx={{ flex: 1, maxWidth: "200px", display: "flex" }}
              >
                <Button
                  variant="contained"
                  onClick={() => onLoadAll(campaignName)}
                  disabled={campaignLoading}
                  fullWidth
                  startIcon={
                    campaignLoading ? <CircularProgress size={16} /> : null
                  }
                  sx={{
                    backgroundColor: "#008069",
                    color: "white",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    minWidth: "140px",
                    "&:hover": {
                      backgroundColor: "#006d5b",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(0, 128, 105, 0.25)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                      backgroundColor: "#008069",
                    },
                  }}
                >
                  {campaignLoading ? "Carregando..." : `Carregar todos`}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default CampaignCard;
